import { useEffect, useMemo, useState } from 'react'
import { sileo } from 'sileo'
import { FacturaAPI } from '../../../../../../../api/facturas.api'
import SectionCard from '../../components/SectionCard'
import SalesNoteEditorModal from './SalesNoteEditorModal'
import SalesNoteListView from './SalesNoteListView'

const SALES_NOTE_STORAGE_PREFIX = 'inventory-sales-note-drafts'
const facturaAPI = new FacturaAPI()

const createEmptyCustomer = () => ({
  businessName: '',
  rut: '',
  contact: '',
  address: '',
  district: '',
  city: ''
})

const createEmptyLine = () => ({
  sourceType: 'manual',
  inventoryItemId: '',
  itemCode: '',
  itemName: '',
  itemDescription: '',
  quantity: 1,
  unit: 'UN',
  unitPrice: '',
  discountPercent: '',
  showDescription: false
})

const cloneLine = line => ({ ...createEmptyLine(), ...line })

const getTodayString = () => new Date().toISOString().split('T')[0]

const createDraftId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const buildStorageKey = businessId => `${SALES_NOTE_STORAGE_PREFIX}:${businessId || 'default'}`

const buildInternalCode = dateIssued => {
  const compactDate = String(dateIssued || getTodayString()).replace(/-/g, '')
  return `NV-${compactDate}-LOCAL`
}

const getLineSubtotal = line => {
  const quantity = Number(line?.quantity ?? 0) || 0
  const unitPrice = Number(line?.unitPrice ?? 0) || 0
  const discountPercent = Number(line?.discountPercent ?? 0) || 0
  const gross = quantity * unitPrice
  const discount = (gross * discountPercent) / 100

  return gross - discount
}

const calculateTotals = (lines, isExempt = false) => {
  const net = (lines || []).reduce((accumulator, line) => accumulator + getLineSubtotal(line), 0)
  const iva = isExempt ? 0 : Math.round(net * 0.19)
  const total = net + iva

  return { net, iva, total }
}

const getVisibleLines = lines =>
  (lines || []).filter(
    line => String(line?.itemName || '').trim() || String(line?.itemCode || '').trim()
  )

const normalizeStoredNotes = payload => {
  if (!Array.isArray(payload)) return []

  return payload
    .filter(note => note && typeof note === 'object')
    .map(note => ({
      id: String(note.id || createDraftId()),
      internalCode: String(note.internalCode || buildInternalCode(note.dateIssued)),
      dateIssued: String(note.dateIssued || getTodayString()),
      dueDate: String(note.dueDate || note.dateIssued || getTodayString()),
      isExempt: Boolean(note.isExempt),
      reference: String(note.reference || ''),
      note: String(note.note || ''),
      customer: {
        ...createEmptyCustomer(),
        ...(note.customer && typeof note.customer === 'object' ? note.customer : {})
      },
      lines:
        Array.isArray(note.lines) && note.lines.length > 0
          ? note.lines.map(cloneLine)
          : [createEmptyLine()],
      status: 'Borrador local',
      createdAt: String(note.createdAt || new Date().toISOString()),
      updatedAt: String(note.updatedAt || note.createdAt || new Date().toISOString())
    }))
    .sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime())
}

const resolveCompanyLabel = currentBusiness => {
  const value =
    currentBusiness?.business_name ??
    currentBusiness?.businessName ??
    currentBusiness?.name ??
    currentBusiness?.nombre ??
    'Empresa'

  return String(value)
}

const resolveCompanyValue = (currentBusiness, keys, fallback = '') => {
  for (const key of keys) {
    const value = currentBusiness?.[key]
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value)
    }
  }

  return fallback
}

const mapEntityToCustomer = entity => {
  const data = entity?.data || entity
  const mainAddress = data?.addresses?.[0] || {}

  return {
    businessName: String(data?.name || ''),
    rut: String(data?.tax_id || ''),
    contact: String(mainAddress?.phone || ''),
    address: String(mainAddress?.address || ''),
    district: String(mainAddress?.district?.name || ''),
    city: String(mainAddress?.district?.city?.name || '')
  }
}

const SalesNoteSection = ({ currentBusiness, data }) => {
  const [view, setView] = useState('list')
  const [activeNoteId, setActiveNoteId] = useState(null)
  const [salesNotes, setSalesNotes] = useState([])
  const [dateIssued, setDateIssued] = useState(getTodayString())
  const [dueDate, setDueDate] = useState(getTodayString())
  const [isExempt, setIsExempt] = useState(false)
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [customer, setCustomer] = useState(createEmptyCustomer())
  const [lines, setLines] = useState([createEmptyLine()])
  const [isCustomerRutLoading, setIsCustomerRutLoading] = useState(false)

  const businessId = currentBusiness?.id || 'default'
  const storageKey = useMemo(() => buildStorageKey(businessId), [businessId])
  const company = useMemo(
    () => ({
      label: resolveCompanyLabel(currentBusiness),
      rut: currentBusiness?.rut || currentBusiness?.tax_id || currentBusiness?.taxId || '',
      email: resolveCompanyValue(currentBusiness, ['email']),
      address: resolveCompanyValue(currentBusiness, ['address', 'direccion']),
      district: resolveCompanyValue(currentBusiness, ['district', 'comuna']),
      city: resolveCompanyValue(currentBusiness, ['city', 'ciudad'])
    }),
    [currentBusiness]
  )
  const internalCode = useMemo(() => buildInternalCode(dateIssued), [dateIssued])
  const visibleLines = useMemo(() => getVisibleLines(lines), [lines])
  const totals = useMemo(() => calculateTotals(visibleLines, isExempt), [isExempt, visibleLines])
  const listedNotes = useMemo(
    () =>
      salesNotes.map(record => {
        const recordVisibleLines = getVisibleLines(record.lines)
        const recordTotals = calculateTotals(recordVisibleLines, Boolean(record.isExempt))

        return {
          ...record,
          customerLabel:
            String(record.customer?.businessName || '').trim() ||
            String(record.customer?.contact || '').trim() ||
            'Sin cliente',
          itemsCount: recordVisibleLines.length,
          totalAmount: recordTotals.total
        }
      }),
    [salesNotes]
  )

  useEffect(() => {
    if (typeof window === 'undefined') return

    try {
      const storedValue = window.localStorage.getItem(storageKey)
      const parsedValue = storedValue ? JSON.parse(storedValue) : []
      setSalesNotes(normalizeStoredNotes(parsedValue))
    } catch (error) {
      console.error('No se pudieron cargar las notas de venta locales', error)
      setSalesNotes([])
    }
  }, [storageKey])

  useEffect(() => {
    if (typeof window === 'undefined') return

    try {
      window.localStorage.setItem(storageKey, JSON.stringify(salesNotes))
    } catch (error) {
      console.error('No se pudieron guardar las notas de venta locales', error)
    }
  }, [salesNotes, storageKey])

  const resetDraftState = nextView => {
    setActiveNoteId(null)
    setDateIssued(getTodayString())
    setDueDate(getTodayString())
    setIsExempt(false)
    setReference('')
    setNote('')
    setCustomer(createEmptyCustomer())
    setLines([createEmptyLine()])
    setView(nextView)
  }

  const applyStoredDraft = (record, nextView) => {
    if (!record) return

    setActiveNoteId(record.id)
    setDateIssued(record.dateIssued || getTodayString())
    setDueDate(record.dueDate || record.dateIssued || getTodayString())
    setIsExempt(Boolean(record.isExempt))
    setReference(record.reference || '')
    setNote(record.note || '')
    setCustomer({
      ...createEmptyCustomer(),
      ...(record.customer || {})
    })
    setLines(
      Array.isArray(record.lines) && record.lines.length > 0
        ? record.lines.map(cloneLine)
        : [createEmptyLine()]
    )
    setView(nextView)
  }

  const updateCustomer = (field, value) => {
    setCustomer(prev => ({ ...prev, [field]: value }))
  }

  const searchCustomerByRut = async rutValue => {
    const rut = String(rutValue || '').trim()

    if (rut.length < 8) return

    setIsCustomerRutLoading(true)

    try {
      const response = await facturaAPI.getSearchEntityRut(rut)
      const nextCustomer = mapEntityToCustomer(response?.data)

      if (!String(nextCustomer.businessName || '').trim()) {
        sileo.warning({
          title: 'Entidad no encontrada',
          description: 'No se encontró un cliente asociado a ese RUT.'
        })
        return
      }

      setCustomer(prev => ({
        ...prev,
        ...nextCustomer
      }))
    } catch (error) {
      const status = error?.response?.status

      if (status === 404) {
        sileo.warning({
          title: 'Entidad no encontrada',
          description: 'No se encontró un cliente asociado a ese RUT.'
        })
        return
      }

      sileo.error({
        title: 'Error al buscar cliente',
        description: error?.response?.data?.message || 'No se pudo consultar la entidad por RUT.'
      })
    } finally {
      setIsCustomerRutLoading(false)
    }
  }

  const updateLine = (index, field, value) => {
    setLines(prev =>
      prev.map((line, lineIndex) => (lineIndex === index ? { ...line, [field]: value } : line))
    )
  }

  const updateLineSourceType = (index, sourceType) => {
    setLines(prev =>
      prev.map((line, lineIndex) => {
        if (lineIndex !== index) return line

        if (sourceType === 'manual') {
          return {
            ...line,
            sourceType,
            inventoryItemId: ''
          }
        }

        return {
          ...line,
          sourceType
        }
      })
    )
  }

  const applyInventoryItem = (index, itemId) => {
    const selectedItem =
      (data.itemOptions || []).find(option => String(option.id) === String(itemId)) || null

    setLines(prev =>
      prev.map((line, lineIndex) => {
        if (lineIndex !== index) return line
        if (!selectedItem) {
          return { ...line, inventoryItemId: '' }
        }

        return {
          ...line,
          inventoryItemId: String(selectedItem.id),
          itemCode: selectedItem.sku || line.itemCode,
          itemName: selectedItem.name || line.itemName,
          unit: selectedItem.unit || line.unit,
          unitPrice: selectedItem.price || line.unitPrice
        }
      })
    )
  }

  const addLine = () => {
    setLines(prev => [...prev, createEmptyLine()])
  }

  const removeLine = index => {
    setLines(prev =>
      prev.length === 1 ? [createEmptyLine()] : prev.filter((_, lineIndex) => lineIndex !== index)
    )
  }

  const handleStartNewNote = () => {
    resetDraftState('form')
  }

  const handleReturnToList = () => {
    setView('list')
  }

  const handlePreview = () => {
    if (!visibleLines.length) {
      sileo.error({
        title: 'Falta el detalle',
        description: 'Agrega al menos una línea con nombre o código para revisar la nota de venta.'
      })
      return
    }

    setView('preview')
  }

  const handleSaveDraft = () => {
    if (!visibleLines.length) {
      sileo.warning({
        title: 'No hay líneas para guardar',
        description: 'Agrega al menos un producto o servicio antes de guardar la nota.'
      })
      return
    }

    const timestamp = new Date().toISOString()
    const nextId = activeNoteId || createDraftId()

    setSalesNotes(prev => {
      const existingRecord = prev.find(record => record.id === nextId)
      const nextRecord = {
        id: nextId,
        internalCode,
        dateIssued,
        dueDate,
        isExempt,
        reference,
        note,
        customer: { ...customer },
        lines: lines.map(cloneLine),
        status: 'Borrador local',
        createdAt: existingRecord?.createdAt || timestamp,
        updatedAt: timestamp
      }

      const nextRecords = existingRecord
        ? prev.map(record => (record.id === nextId ? nextRecord : record))
        : [nextRecord, ...prev]

      return nextRecords.sort(
        (left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime()
      )
    })

    setActiveNoteId(nextId)
    setView('list')

    sileo.success({
      title: activeNoteId ? 'Nota actualizada' : 'Nota creada',
      description: 'La nota de venta quedó guardada localmente en este navegador.'
    })
  }

  const draftLines = useMemo(
    () =>
      lines.map(line => ({
        ...line,
        subtotal: getLineSubtotal(line)
      })),
    [lines]
  )

  const draft = useMemo(
    () => ({
      internalCode,
      dateIssued,
      dueDate,
      isExempt,
      reference,
      note,
      customer,
      lines: draftLines
    }),
    [customer, dateIssued, draftLines, dueDate, internalCode, isExempt, note, reference]
  )

  return (
    <SectionCard
      title='Nota de venta'
      description='Explora borradores locales y crea nuevas notas de venta internas.'
    >
      <SalesNoteListView
        notes={listedNotes}
        onCreate={handleStartNewNote}
        onPreview={record => applyStoredDraft(record, 'preview')}
        onEdit={record => applyStoredDraft(record, 'form')}
      />

      <SalesNoteEditorModal
        open={view !== 'list'}
        view={view}
        currentBusiness={currentBusiness}
        company={company}
        draft={draft}
        totals={totals}
        itemOptions={data.itemOptions || []}
        onClose={handleReturnToList}
        onPreview={handlePreview}
        onSaveDraft={handleSaveDraft}
        onStartNewNote={handleStartNewNote}
        onShowForm={() => setView('form')}
        onCustomerChange={updateCustomer}
        isCustomerRutLoading={isCustomerRutLoading}
        onSearchCustomerByRut={searchCustomerByRut}
        onDateIssuedChange={setDateIssued}
        onDueDateChange={setDueDate}
        onIsExemptChange={setIsExempt}
        onReferenceChange={setReference}
        onNoteChange={setNote}
        onLineChange={updateLine}
        onLineSourceTypeChange={updateLineSourceType}
        onInventoryItemApply={applyInventoryItem}
        onAddLine={addLine}
        onRemoveLine={removeLine}
      />
    </SectionCard>
  )
}

export default SalesNoteSection
